/**
 * MarketDetailModal — chart view for global market indices / commodities.
 * 
 * Features:
 *   - Price chart with period selectors
 *   - Single-metric chart mode (Nifty-style metric tabs)
 *   - Performance Metrics panel (CAGR, volatility, drawdown, Sharpe)
 *   - Enhanced Valuation panel with provenance labels
 *   - Alert creation button
 */

import React, { useState, useEffect, useRef, useMemo } from 'react';
import {
    getMarketsHistory,
    getMarketsFundamentals,
    getMarketsAnalytics,
    getValuationSeries,
    getMarketAlerts,
    createMarketAlert,
    deleteMarketAlert,
} from '../../services/marketsService';
import {
    ResponsiveContainer,
    AreaChart,
    Area,
    XAxis,
    YAxis,
    Tooltip,
    CartesianGrid,
    ReferenceLine,
} from 'recharts';
import {
    METRIC_DEFS,
    getMetricDef,
    getSeriesForMetric,
    formatMetricValue,
    getYAxisDomainForSeries,
} from './marketChartUtils';
import '../../styles/FundsPage.css';

const PERIODS = ['1D', '5D', '1M', '3M', '6M', '1Y', '3Y', '5Y', '10Y'];
const unwrap = (res) => (res && typeof res === 'object' && 'data' in res ? res.data : res);
const fmt = (v) => (v == null ? '—' : Number(v).toFixed(2));
const fmtPct = (v) => (v == null ? '—' : `${v >= 0 ? '+' : ''}${Number(v).toFixed(2)}%`);

const ALERT_METRICS = [
    { value: 'price', label: 'Price' },
    { value: 'pe', label: 'Trailing PE' },
    { value: 'pb', label: 'Price/Book' },
    { value: 'cape', label: 'CAPE (P/E10)' },
    { value: 'dividendYield', label: 'Dividend Yield %' },
];

const ALERT_CONDITIONS = [
    { value: 'above', label: 'Goes Above' },
    { value: 'below', label: 'Goes Below' },
];

const MarketDetailModal = ({ item, onClose }) => {
    const [period, setPeriod] = useState('1Y');
    const [chartData, setChartData] = useState([]);
    const [loading, setLoading] = useState(false);
    const [meta, setMeta] = useState(null);
    const [fundamentals, setFundamentals] = useState(null);
    const [analytics, setAnalytics] = useState(null);
    const [analyticsLoading, setAnalyticsLoading] = useState(false);
    const seqRef = useRef(0);

    // Valuation series state
    const [valuationSeries, setValuationSeries] = useState(null);
    const [valuationLoading, setValuationLoading] = useState(false);
    const [selectedMetric, setSelectedMetric] = useState('close');

    // Alerts state
    const [alerts, setAlerts] = useState([]);
    const [showAlertDialog, setShowAlertDialog] = useState(false);
    const [alertForm, setAlertForm] = useState({ metric: 'price', condition: 'above', threshold: '' });
    const [alertSaving, setAlertSaving] = useState(false);

    const hasFundamentals = Boolean(item?.etfProxy);
    const isCommodity = !hasFundamentals;

    // Fetch history
    useEffect(() => {
        if (!item?.symbol) return;
        const controller = new AbortController();
        const seq = ++seqRef.current;
        setLoading(true);

        getMarketsHistory(item.symbol, period, { signal: controller.signal })
            .then((raw) => {
                if (seq !== seqRef.current) return;
                const result = unwrap(raw);
                if (!result?.data) return;
                setChartData(result.data.map((d) => ({ date: d.date, close: d.close, volume: d.volume })));
                setMeta(result);
            })
            .catch((err) => {
                if (seq !== seqRef.current) return;
                if (err?.name === 'CanceledError' || err?.code === 'ERR_CANCELED') return;
                console.error('Market history fetch failed:', err);
                setChartData([]);
            })
            .finally(() => {
                if (seq === seqRef.current) setLoading(false);
            });

        return () => controller.abort();
    }, [item?.symbol, period]);

    // Fetch fundamentals (once, only for equity indices)
    useEffect(() => {
        if (!item?.symbol || !hasFundamentals) return;
        getMarketsFundamentals(item.symbol)
            .then((raw) => {
                const result = unwrap(raw);
                setFundamentals(result);
            })
            .catch(() => setFundamentals(null));
    }, [item?.symbol, hasFundamentals]);

    // Fetch analytics
    useEffect(() => {
        if (!item?.symbol) return;
        setAnalyticsLoading(true);
        getMarketsAnalytics(item.symbol, 'MAX')
            .then((raw) => {
                const result = unwrap(raw);
                setAnalytics(result);
            })
            .catch(() => setAnalytics(null))
            .finally(() => setAnalyticsLoading(false));
    }, [item?.symbol]);

    // Fetch valuation series (for metric tabs)
    useEffect(() => {
        if (!item?.symbol || isCommodity) return;
        setValuationLoading(true);
        getValuationSeries(item.symbol, 10)
            .then((raw) => {
                const result = unwrap(raw);
                setValuationSeries(result);
            })
            .catch(() => setValuationSeries(null))
            .finally(() => setValuationLoading(false));
    }, [item?.symbol, isCommodity]);

    useEffect(() => {
        setSelectedMetric('close');
    }, [item?.symbol]);

    // Fetch alerts for this symbol
    useEffect(() => {
        if (!item?.symbol) return;
        getMarketAlerts(item.symbol)
            .then((raw) => {
                const data = unwrap(raw);
                setAlerts(Array.isArray(data) ? data : []);
            })
            .catch(() => setAlerts([]));
    }, [item?.symbol]);

    const metricOptions = useMemo(() => {
        if (isCommodity) return METRIC_DEFS.filter((m) => m.key === 'close');
        return METRIC_DEFS.map((m) => {
            if (m.key === 'close') return { ...m, disabled: false };
            const available = Boolean(valuationSeries?.availability?.[m.key]?.hasTimeSeries);
            return {
                ...m,
                disabled: !available,
                disabledReason: valuationSeries?.availability?.[m.key]?.reason || valuationSeries?.availability?.[m.key]?.configHint || 'No data available',
            };
        });
    }, [isCommodity, valuationSeries]);

    useEffect(() => {
        const selected = metricOptions.find((m) => m.key === selectedMetric);
        if (selected && !selected.disabled) return;
        setSelectedMetric('close');
    }, [metricOptions, selectedMetric]);

    const selectedMetricDef = useMemo(() => getMetricDef(selectedMetric), [selectedMetric]);

    const selectedSeries = useMemo(() => (
        getSeriesForMetric({ metricKey: selectedMetric, chartData, valuationSeries })
    ), [selectedMetric, chartData, valuationSeries]);

    const yAxisDomain = useMemo(() => getYAxisDomainForSeries(selectedSeries), [selectedSeries]);

    const formatDate = (dateStr) => {
        if (!dateStr) return '';
        const d = new Date(dateStr);
        if (['1D', '5D'].includes(period)) {
            return d.toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit' });
        }
        return d.toLocaleDateString('en-US', { day: '2-digit', month: 'short', year: '2-digit' });
    };

    const handleOverlayClick = (e) => {
        if (e.target === e.currentTarget) onClose();
    };

    const metricValues = useMemo(
        () => selectedSeries.map((d) => Number(d.value)).filter(Number.isFinite),
        [selectedSeries]
    );
    const metricAvg = metricValues.length
        ? metricValues.reduce((sum, value) => sum + value, 0) / metricValues.length
        : null;
    const metricMedian = metricValues.length
        ? (() => {
            const sorted = [...metricValues].sort((a, b) => a - b);
            const mid = Math.floor(sorted.length / 2);
            return sorted.length % 2 === 0
                ? (sorted[mid - 1] + sorted[mid]) / 2
                : sorted[mid];
        })()
        : null;
    const showReferenceLines = selectedMetric !== 'close' && metricValues.length > 0;

    // Alert handlers
    const handleCreateAlert = async () => {
        if (!alertForm.threshold || isNaN(Number(alertForm.threshold))) return;
        setAlertSaving(true);
        try {
            const raw = await createMarketAlert({
                symbol: item.symbol,
                metric: alertForm.metric,
                condition: alertForm.condition,
                threshold: Number(alertForm.threshold),
            });
            const result = unwrap(raw);
            if (result && !result.error) {
                setAlerts((prev) => [result, ...prev]);
                setShowAlertDialog(false);
                setAlertForm({ metric: 'price', condition: 'above', threshold: '' });
            }
        } catch (err) {
            console.error('Failed to create alert:', err);
        } finally {
            setAlertSaving(false);
        }
    };

    const handleDeleteAlert = async (alertId) => {
        try {
            await deleteMarketAlert(alertId);
            setAlerts((prev) => prev.filter((a) => a.id !== alertId));
        } catch (err) {
            console.error('Failed to delete alert:', err);
        }
    };

    // Custom tooltip for selected metric only
    const CustomTooltip = ({ active, payload, label }) => {
        if (!active || !payload?.length) return null;
        const value = payload[0]?.value;
        return (
            <div style={{
                background: 'var(--bg-2)',
                border: '1px solid var(--border)',
                borderRadius: 8,
                padding: '8px 12px',
                color: 'var(--text-1)',
                fontSize: '0.82rem',
            }}>
                <div style={{ marginBottom: 4, fontWeight: 600 }}>{formatDate(label)}</div>
                <div style={{ color: selectedMetricDef.color, display: 'flex', gap: 8 }}>
                    <span>{selectedMetricDef.label}:</span>
                    <span style={{ fontWeight: 600 }}>{formatMetricValue(selectedMetric, value)}</span>
                </div>
            </div>
        );
    };

    return (
        <div className="index-detail-overlay" onClick={handleOverlayClick}>
            <div className="index-detail-panel" onClick={(e) => e.stopPropagation()}>
                {/* Header */}
                <div className="index-detail-header">
                    <div>
                        <h2 className="index-detail-title">{item.name}</h2>
                        <span className="index-card-symbol" style={{ fontSize: '0.85rem' }}>
                            {item.symbol}
                        </span>
                    </div>
                    <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
                        {!isCommodity && (
                            <button
                                className="market-alert-btn"
                                onClick={() => setShowAlertDialog(true)}
                                title="Create Alert"
                            >
                                🔔 Alert
                            </button>
                        )}
                        <button className="index-detail-close" onClick={onClose} aria-label="Close">
                            ✕
                        </button>
                    </div>
                </div>

                {/* Period selectors */}
                <div className="index-period-selectors">
                    {PERIODS.map((p) => (
                        <button
                            key={p}
                            className={`index-period-btn ${period === p ? 'index-period-btn--active' : ''}`}
                            onClick={() => setPeriod(p)}
                        >
                            {p}
                        </button>
                    ))}
                </div>

                {/* Metric selectors (single metric mode) */}
                <div className="index-period-selectors" style={{ marginTop: '-0.4rem' }}>
                    {metricOptions.map((m) => (
                        <button
                            key={m.key}
                            className={`index-period-btn ${selectedMetric === m.key ? 'index-period-btn--active' : ''}`}
                            onClick={() => setSelectedMetric(m.key)}
                            disabled={m.disabled}
                            title={m.disabled ? (m.disabledReason || 'No data available') : m.label}
                            style={m.disabled ? { opacity: 0.5, cursor: 'not-allowed' } : undefined}
                        >
                            {m.label}{m.disabled ? ' (N/A)' : ''}
                        </button>
                    ))}
                    {valuationLoading && !isCommodity && (
                        <span style={{ fontSize: '0.75rem', color: 'var(--text-2)', marginLeft: 8 }}>Loading metrics…</span>
                    )}
                </div>

                {/* Chart */}
                <div className="index-chart-container">
                    {loading ? (
                        <div className="funds-loading" style={{ paddingTop: '8rem' }}>
                            Loading chart data…
                        </div>
                    ) : chartData.length === 0 ? (
                        <div className="funds-empty">
                            <p>No chart data available for this period.</p>
                        </div>
                    ) : selectedMetric !== 'close' && valuationLoading ? (
                        <div className="funds-loading" style={{ paddingTop: '8rem' }}>
                            Loading valuation data…
                        </div>
                    ) : selectedSeries.length === 0 ? (
                        <div className="funds-empty">
                            <p>No data available for {selectedMetricDef.label}.</p>
                        </div>
                    ) : (
                        <ResponsiveContainer width="100%" height="100%">
                            <AreaChart data={selectedSeries}>
                                <defs>
                                    <linearGradient id="mktGrad" x1="0" y1="0" x2="0" y2="1">
                                        <stop offset="5%" stopColor={selectedMetricDef.color} stopOpacity={0.3} />
                                        <stop offset="95%" stopColor={selectedMetricDef.color} stopOpacity={0} />
                                    </linearGradient>
                                </defs>
                                <CartesianGrid strokeDasharray="3 3" stroke="var(--border)" vertical={false} />
                                <XAxis
                                    dataKey="date"
                                    tickFormatter={formatDate}
                                    tick={{ fontSize: 11, fill: 'var(--text-2)' }}
                                    axisLine={{ stroke: 'var(--border)' }}
                                    tickLine={false}
                                    minTickGap={40}
                                />
                                <YAxis
                                    domain={yAxisDomain}
                                    tick={{ fontSize: 11, fill: 'var(--text-2)' }}
                                    axisLine={false}
                                    tickLine={false}
                                    tickFormatter={(v) => formatMetricValue(selectedMetric, v)}
                                    width={70}
                                />
                                <Tooltip content={<CustomTooltip />} />

                                {showReferenceLines && metricAvg != null && (
                                    <ReferenceLine
                                        y={metricAvg}
                                        stroke="var(--warning)"
                                        strokeDasharray="4 4"
                                        label={{ value: `Avg ${formatMetricValue(selectedMetric, metricAvg)}`, fill: 'var(--warning)', fontSize: 11 }}
                                    />
                                )}
                                {showReferenceLines && metricMedian != null && (
                                    <ReferenceLine
                                        y={metricMedian}
                                        stroke="var(--brand-2)"
                                        strokeDasharray="4 4"
                                        label={{ value: `Median ${formatMetricValue(selectedMetric, metricMedian)}`, fill: 'var(--brand-2)', fontSize: 11 }}
                                    />
                                )}

                                <Area
                                    type="monotone"
                                    dataKey="value"
                                    name={selectedMetricDef.label}
                                    stroke={selectedMetricDef.color}
                                    strokeWidth={2}
                                    fillOpacity={1}
                                    fill="url(#mktGrad)"
                                    dot={false}
                                    activeDot={{ r: 4 }}
                                />

                                {selectedMetric === 'pe' && fundamentals?.trailingPE && (
                                    <ReferenceLine
                                        y={fundamentals.trailingPE}
                                        stroke="#9C27B0"
                                        strokeDasharray="5 5"
                                        label={{
                                            value: `PE ${fmt(fundamentals.trailingPE)}`,
                                            position: 'right',
                                            fill: '#9C27B0',
                                            fontSize: 10,
                                        }}
                                    />
                                )}
                            </AreaChart>
                        </ResponsiveContainer>
                    )}
                </div>

                {/* Summary row */}
                {meta && (
                    <div style={{ fontSize: '0.8rem', color: 'var(--text-2)', marginTop: '0.5rem' }}>
                        Last: <strong style={{ color: 'var(--text-1)' }}>{meta.last?.toLocaleString('en-US')}</strong>
                        {' | '}Change: <strong style={{ color: (meta.change ?? 0) >= 0 ? 'var(--success)' : 'var(--danger)' }}>
                            {(meta.change ?? 0) >= 0 ? '+' : ''}{meta.change} ({(meta.changePercent ?? 0) >= 0 ? '+' : ''}{meta.changePercent}%)
                        </strong>
                        {meta.lastUpdated && <> | Updated: {new Date(meta.lastUpdated).toLocaleString()}</>}
                    </div>
                )}

                {/* Metrics panel */}
                <div className="market-metrics-panel">
                    {/* Analytics / CAGR */}
                    <div className="market-metrics-section">
                        <h4 className="market-metrics-heading">Performance Metrics</h4>
                        {analyticsLoading ? (
                            <div style={{ fontSize: '0.78rem', color: 'var(--text-2)' }}>Computing…</div>
                        ) : analytics && !analytics.error ? (
                            <div className="market-metrics-grid">
                                <div className="market-metric-item">
                                    <span className="market-metric-label">CAGR 1Y</span>
                                    <span className="market-metric-value">{fmtPct(analytics.cagr_1y)}</span>
                                </div>
                                <div className="market-metric-item">
                                    <span className="market-metric-label">CAGR 3Y</span>
                                    <span className="market-metric-value">{fmtPct(analytics.cagr_3y)}</span>
                                </div>
                                <div className="market-metric-item">
                                    <span className="market-metric-label">CAGR 5Y</span>
                                    <span className="market-metric-value">{fmtPct(analytics.cagr_5y)}</span>
                                </div>
                                <div className="market-metric-item">
                                    <span className="market-metric-label">CAGR 10Y</span>
                                    <span className="market-metric-value">{fmtPct(analytics.cagr_10y)}</span>
                                </div>
                                <div className="market-metric-item">
                                    <span className="market-metric-label">Volatility</span>
                                    <span className="market-metric-value">{analytics.volatility != null ? `${analytics.volatility}%` : '—'}</span>
                                </div>
                                <div className="market-metric-item">
                                    <span className="market-metric-label">Max Drawdown</span>
                                    <span className="market-metric-value" style={{ color: analytics.maxDrawdown != null ? 'var(--danger)' : undefined }}>
                                        {analytics.maxDrawdown != null ? `${analytics.maxDrawdown}%` : '—'}
                                    </span>
                                </div>
                                <div className="market-metric-item">
                                    <span className="market-metric-label">Sharpe</span>
                                    <span className="market-metric-value">{analytics.sharpe != null ? analytics.sharpe : '—'}</span>
                                </div>
                            </div>
                        ) : (
                            <div style={{ fontSize: '0.78rem', color: 'var(--text-2)' }}>Analytics not available.</div>
                        )}
                    </div>

                    {/* Enhanced Valuation panel */}
                    <div className="market-metrics-section">
                        <h4 className="market-metrics-heading">Valuation</h4>
                        {isCommodity ? (
                            <div style={{ fontSize: '0.78rem', color: 'var(--text-2)' }}>
                                PE / PB / EPS not applicable for commodities.
                            </div>
                        ) : fundamentals ? (
                            <div className="market-metrics-grid">
                                <div className="market-metric-item">
                                    <span className="market-metric-label">Trailing PE</span>
                                    <span className="market-metric-value">{fmt(fundamentals.trailingPE)}</span>
                                </div>
                                <div className="market-metric-item">
                                    <span className="market-metric-label">Forward PE</span>
                                    <span className="market-metric-value">{fmt(fundamentals.forwardPE)}</span>
                                </div>
                                <div className="market-metric-item">
                                    <span className="market-metric-label">P/B</span>
                                    <span className="market-metric-value">{fmt(fundamentals.priceToBook)}</span>
                                </div>
                                <div className="market-metric-item">
                                    <span className="market-metric-label">EPS</span>
                                    <span className="market-metric-value">{fmt(fundamentals.trailingEps)}</span>
                                </div>

                                {/* Dividend Yield */}
                                <div className="market-metric-item">
                                    <span className="market-metric-label">Div Yield</span>
                                    <span className="market-metric-value">
                                        {valuationSeries?.currentValues
                                            ? (valuationSeries.currentValues.dividendYield != null
                                                ? `${fmt(valuationSeries.currentValues.dividendYield)}%`
                                                : 'N/A')
                                            : valuationLoading ? '…' : 'N/A'}
                                    </span>
                                </div>

                                {/* CAPE */}
                                <div className="market-metric-item">
                                    <span className="market-metric-label">CAPE</span>
                                    <span className="market-metric-value">
                                        {valuationSeries?.availability?.cape?.hasTimeSeries
                                            ? fmt(valuationSeries.series.cape?.[valuationSeries.series.cape.length - 1]?.cape)
                                            : <span className="market-metric-na" title="CAPE only available for S&P 500">N/A</span>}
                                    </span>
                                </div>

                                {fundamentals.proxyTicker && (
                                    <div style={{ gridColumn: '1 / -1', fontSize: '0.7rem', color: 'var(--text-2)', opacity: 0.8, marginTop: '0.2rem' }}>
                                        Source: ETF proxy {fundamentals.proxyTicker}
                                        {valuationSeries?.availability?.cape?.sourceLabel && ` | CAPE: ${valuationSeries.availability.cape.sourceLabel}`}
                                        {valuationSeries?.availability?.pe?.sourceLabel && ` | PE: ${valuationSeries.availability.pe.sourceLabel}`}
                                    </div>
                                )}

                                {/* Data availability notices with source provenance */}
                                {valuationSeries && !valuationLoading && (
                                    <div style={{ gridColumn: '1 / -1', fontSize: '0.68rem', color: 'var(--text-2)', opacity: 0.7, marginTop: '0.3rem' }}>
                                        {valuationSeries.availability?.pe?.hasTimeSeries && (
                                            <div>✓ PE: Historical series from {valuationSeries.availability.pe.sourceLabel || 'API'} ({valuationSeries.availability.pe.dataPoints} pts)</div>
                                        )}
                                        {valuationSeries.availability?.pe && !valuationSeries.availability.pe.hasTimeSeries && valuationSeries.availability.pe.configHint && (
                                            <div>ℹ PE: {valuationSeries.availability.pe.configHint}</div>
                                        )}
                                        {valuationSeries.availability?.pb?.hasTimeSeries && (
                                            <div>✓ P/B: Historical series from {valuationSeries.availability.pb.sourceLabel || 'API'} ({valuationSeries.availability.pb.dataPoints} pts)</div>
                                        )}
                                        {valuationSeries.availability?.eps?.hasTimeSeries && (
                                            <div>✓ EPS: Historical series from {valuationSeries.availability.eps.sourceLabel || 'API'} ({valuationSeries.availability.eps.dataPoints} pts)</div>
                                        )}
                                        {valuationSeries.availability?.dividendYield?.hasTimeSeries && (
                                            <div>✓ Div Yield: Historical series from {valuationSeries.availability.dividendYield.sourceLabel || 'ETF proxy'} ({valuationSeries.availability.dividendYield.dataPoints} pts)</div>
                                        )}
                                        {valuationSeries.availability?.cape?.hasTimeSeries && (
                                            <div>✓ CAPE: Historical series from {valuationSeries.availability.cape.sourceLabel || 'Shiller/Yale'} ({valuationSeries.availability.cape.dataPoints} pts)</div>
                                        )}
                                        {valuationSeries.availability?.cape && !valuationSeries.availability.cape.hasTimeSeries && (
                                            <div>ℹ CAPE: {valuationSeries.availability.cape.reason}</div>
                                        )}
                                        {valuationSeries.availability?.treasury10y?.hasTimeSeries && (
                                            <div>✓ 10Y Treasury: {valuationSeries.availability.treasury10y.sourceLabel} ({valuationSeries.availability.treasury10y.dataPoints} pts)</div>
                                        )}
                                    </div>
                                )}
                            </div>
                        ) : (
                            <div style={{ fontSize: '0.78rem', color: 'var(--text-2)' }}>Loading…</div>
                        )}
                    </div>

                    {/* Active Alerts */}
                    {alerts.length > 0 && (
                        <div className="market-metrics-section">
                            <h4 className="market-metrics-heading">Active Alerts</h4>
                            <div className="market-alerts-list">
                                {alerts.map((alert) => (
                                    <div key={alert.id} className="market-alert-item">
                                        <span className="market-alert-desc">
                                            {alert.metric.toUpperCase()} {alert.condition} {Number(alert.threshold).toFixed(2)}
                                        </span>
                                        <span className={`market-alert-status ${alert.triggered ? 'market-alert-triggered' : 'market-alert-active'}`}>
                                            {alert.triggered ? '✓ Triggered' : '● Active'}
                                        </span>
                                        <button
                                            className="market-alert-delete"
                                            onClick={() => handleDeleteAlert(alert.id)}
                                            title="Delete alert"
                                        >
                                            ✕
                                        </button>
                                    </div>
                                ))}
                            </div>
                        </div>
                    )}
                </div>

                {/* Alert Creation Dialog */}
                {showAlertDialog && (
                    <div className="market-alert-dialog-overlay" onClick={() => setShowAlertDialog(false)}>
                        <div className="market-alert-dialog" onClick={(e) => e.stopPropagation()}>
                            <h3>Create Alert for {item.symbol}</h3>
                            <div className="market-alert-form">
                                <label>
                                    <span>Metric</span>
                                    <select
                                        value={alertForm.metric}
                                        onChange={(e) => setAlertForm((f) => ({ ...f, metric: e.target.value }))}
                                    >
                                        {ALERT_METRICS.map((m) => (
                                            <option key={m.value} value={m.value}>{m.label}</option>
                                        ))}
                                    </select>
                                </label>
                                <label>
                                    <span>Condition</span>
                                    <select
                                        value={alertForm.condition}
                                        onChange={(e) => setAlertForm((f) => ({ ...f, condition: e.target.value }))}
                                    >
                                        {ALERT_CONDITIONS.map((c) => (
                                            <option key={c.value} value={c.value}>{c.label}</option>
                                        ))}
                                    </select>
                                </label>
                                <label>
                                    <span>Threshold</span>
                                    <input
                                        type="number"
                                        step="any"
                                        value={alertForm.threshold}
                                        onChange={(e) => setAlertForm((f) => ({ ...f, threshold: e.target.value }))}
                                        placeholder="e.g. 7000"
                                    />
                                </label>
                            </div>
                            <div className="market-alert-dialog-actions">
                                <button onClick={() => setShowAlertDialog(false)}>Cancel</button>
                                <button
                                    className="market-alert-save-btn"
                                    onClick={handleCreateAlert}
                                    disabled={alertSaving || !alertForm.threshold}
                                >
                                    {alertSaving ? 'Saving…' : 'Create Alert'}
                                </button>
                            </div>
                        </div>
                    </div>
                )}
            </div>
        </div>
    );
};

export default MarketDetailModal;
